/**
 * Site-audit share reads.
 *
 * `claim` redeems the `share=<shareId>` a report's sign-up CTA carried,
 * turning the public audit into a project with its findings as issues.
 * Server: POST /api/site-audits/:shareId/claim (routes/siteAudits.ts).
 *
 * `shareStats` is the unauthenticated read that lets the sign-in page NAME
 * the report a visitor arrived from, before they have an account.
 */
import { fetchApi, fetchApiEnvelope } from './client';
import { log } from '@/lib/logger';

/** Generous: the server inserts one ticket per violation, serially. */
const CLAIM_REQUEST_TIMEOUT_MS = 30_000;

/**
 * Tight on purpose. This read only decorates the sign-in page; a slow one must
 * expire quietly rather than hold a notice open above a form the visitor is
 * already typing into.
 */
const SHARE_STATS_TIMEOUT_MS = 4_000;

export interface ClaimSiteAuditResult {
  projectId: string;
  issueCount: number;
  alreadyClaimed: boolean;
}

/** Subset of GET /site-audits/:shareId/stats this app reads. */
export interface SiteAuditShareStats {
  shareId: string;
  /** The audited page's URL. Public: it is printed on the report itself. */
  url: string;
  viewCount: number;
}

export const siteAuditsApi = {
  /**
   * No token: the endpoint is public (routes/siteAudits.ts), which is what
   * makes it usable on the signed-out sign-in page. 404 means a dead or
   * mistyped share, which the caller handles by rendering nothing.
   *
   * fetchApiEnvelope, NOT fetchApi. This route answers with a BARE object
   * (`{ shareId, url, viewCount, ... }`), not the `{ data }` envelope the rest
   * of the API uses, because it also serves the outreach tooling. fetchApi
   * would unwrap a `data` key that is not there and resolve to `undefined` --
   * a silent success carrying nothing, which is what shipped for one build and
   * showed up only when the page was driven in a real browser.
   */
  shareStats: async (shareId: string): Promise<SiteAuditShareStats> => {
    log.info('[api/siteAudits] shareStats', { shareId });
    const body = await fetchApiEnvelope<never>(`/site-audits/${encodeURIComponent(shareId)}/stats`, {
      timeoutMs: SHARE_STATS_TIMEOUT_MS,
      expectedErrorStatuses: [404],
    });
    return body as unknown as SiteAuditShareStats;
  },

  /**
   * Idempotent per team (a repeat returns alreadyClaimed=true with the
   * existing project). 404 (bad shareId) and 403 (no team yet) are outcomes
   * useSiteAuditClaim surfaces itself, so they stay off console.error.
   */
  claim: (token: string | undefined, shareId: string): Promise<ClaimSiteAuditResult> => {
    log.info('[api/siteAudits] claim', { shareId });
    return fetchApi<ClaimSiteAuditResult>(`/site-audits/${encodeURIComponent(shareId)}/claim`, {
      token,
      method: 'POST',
      timeoutMs: CLAIM_REQUEST_TIMEOUT_MS,
      expectedErrorStatuses: [403, 404, 422],
    });
  },
};
